<template>
  <div class="detail-columns" :class="{ 'detail-columns--row': isRow }">
    <!-- Content column (left on desktop, top on mobile) -->
    <div class="detail-columns__content" :style="contentStyle">
      <slot name="content" />
    </div>

    <!-- Divider: vertical between columns on desktop, horizontal when stacked -->
    <v-divider :vertical="isRow" color="neutral50" />

    <!-- Comments column (right on desktop, bottom on mobile) -->
    <div class="detail-columns__comments bg-neutral0">
      <slot name="comments" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useDetailNarrow } from '@/composables/useDetailNarrow'

const props = defineProps({
  // Background color for the content column. Fills the full (stretched) column
  // height so tinted notes keep their look; omit for a neutral column.
  contentBackground: {
    type: String,
    default: null,
  },
})

const narrow = useDetailNarrow()

// Side-by-side from md (>=960px) up; stacked below. 960 is the one breakpoint
// where the SCSS and Vuetify JS scales agree. Measured on the screen, or on the
// box a host shows the page in (the presentation stage).
const isRow = computed(() => !narrow.value)

const contentStyle = computed(() =>
  props.contentBackground ? { backgroundColor: props.contentBackground } : {}
)
</script>

<style lang="scss" scoped>
// Split ratio of the two columns on desktop.
$content-basis: 50%;
$comments-basis: 50%;

.detail-columns {
  display: flex;
  flex-direction: column;
  min-height: 100%;

  &--row {
    flex-direction: row;
    align-items: stretch; // equal-height columns
  }
}

.detail-columns__content {
  flex-shrink: 0;
}

.detail-columns__comments {
  flex: 1;
}

.detail-columns--row {
  .detail-columns__content {
    flex: 0 0 $content-basis;
    max-width: $content-basis;
  }

  .detail-columns__comments {
    flex: 0 0 $comments-basis;
    max-width: $comments-basis;
  }
}
</style>
